/** Detalle de requerimiento: lectura + editar + acciones + observaciones + recorrido. */
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { MobileLayout } from '@/components/MobileLayout';
import { MobileButton } from '@/components/MobileButton';
import { MobileIcon } from '@/components/MobileIcon';
import { MobileTextarea } from '@/components/MobileInput';
import { SectionCard } from '@/components/SectionCard';
import { StatusBadge } from '@/components/StatusBadge';
import { FileCapture } from '@/components/FileCapture';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ServerActionRunner } from '@/components/ServerActionRunner';
import { CardListSkeleton } from '@/components/LoadingSkeleton';
import { EmptyState } from '@/components/EmptyState';
import {
  useCreateObservationMutation,
  useDeleteObservationMutation,
  useMobileRequirementAttachmentsQuery,
  useMobileRequirementContextQuery,
  useMobileRequirementObservationsQuery,
  useMobileRequirementQuery,
  useRequirementActionMutation,
  useUpdateObservationMutation,
  useUploadRequirementAttachmentsMutation,
} from '@/hooks/useMobileRequirements';
import { useMobileAuth } from '@/state/MobileAuthContext';
import { extractErrorMessage } from '@/services/mobileApiClient';
import { formatRelativeDate } from '@/utils/format';
import { hapticSuccess } from '@/utils/haptic';

/** Acciones de flujo visibles en el móvil (observe/resolve quedan en la web). */
const MOBILE_REQUIREMENT_ACTIONS = [
  'submit_review',
  'send_to_design',
  'send_to_budget',
  'complete',
  'cancel',
];

function formatDate(value: string | undefined): string {
  if (!value) return '—';
  const date = new Date(value.length <= 10 ? `${value}T00:00:00` : value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function typeLabel(type: string | undefined): string {
  if (type === 'project') return 'Proyecto';
  return 'Estandar';
}

export function RequirementDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = useMobileAuth();
  const requirementId = id ?? null;
  const requirementQuery = useMobileRequirementQuery(requirementId);
  const contextQuery = useMobileRequirementContextQuery(requirementId);
  const attachmentsQuery = useMobileRequirementAttachmentsQuery(requirementId);
  const observationsQuery = useMobileRequirementObservationsQuery(requirementId);
  const uploadMutation = useUploadRequirementAttachmentsMutation();
  const actionMutation = useRequirementActionMutation(requirementId ?? '');
  const createObservation = useCreateObservationMutation(requirementId ?? '');
  const updateObservation = useUpdateObservationMutation(requirementId ?? '');
  const deleteObservation = useDeleteObservationMutation(requirementId ?? '');
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [newComment, setNewComment] = useState('');
  const [commentError, setCommentError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingComment, setEditingComment] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const requirement = requirementQuery.data;
  const canEdit = hasPermission('customer_requirements.update');
  const canObserve = hasPermission('customer_requirements.observe');

  async function handleFiles(files: File[]) {
    if (!requirementId || files.length === 0) return;
    setUploadError(null);
    try {
      await uploadMutation.mutateAsync({ requirementId, files });
      hapticSuccess();
      toast.success(files.length === 1 ? 'Adjunto subido correctamente' : 'Adjuntos subidos correctamente');
    } catch (error) {
      setUploadError(extractErrorMessage(error));
    }
  }

  async function handleExecuteAction(actionCode: string, body: { reason?: string }, revision?: number) {
    return actionMutation.mutateAsync({ action: actionCode, body, revision });
  }

  function handleActionSuccess(actionCode: string, result: unknown) {
    hapticSuccess();
    if (actionCode === 'send_to_budget') {
      const budgetId =
        result !== null && typeof result === 'object'
          ? String((result as { budget_id?: unknown }).budget_id ?? '')
          : '';
      toast.success('Presupuesto creado correctamente');
      if (budgetId) {
        navigate(`/m/budgets/${budgetId}`);
        return;
      }
    } else {
      toast.success('Acción ejecutada correctamente');
    }
    void requirementQuery.refetch();
    void contextQuery.refetch();
  }

  async function handleCreateObservation() {
    const comment = newComment.trim();
    if (comment.length < 3) {
      setCommentError('La observación debe tener al menos 3 caracteres');
      return;
    }
    setCommentError(null);
    try {
      await createObservation.mutateAsync(comment);
      setNewComment('');
      hapticSuccess();
      toast.success('Observación agregada correctamente');
    } catch (error) {
      setCommentError(extractErrorMessage(error));
    }
  }

  function startEditing(observationId: string, comment: string) {
    setEditingId(observationId);
    setEditingComment(comment);
  }

  async function handleSaveEditing(observationId: string) {
    const comment = editingComment.trim();
    if (comment.length < 3) {
      toast.error('La observación debe tener al menos 3 caracteres');
      return;
    }
    try {
      await updateObservation.mutateAsync({ observationId, comment });
      setEditingId(null);
      setEditingComment('');
      hapticSuccess();
      toast.success('Observación actualizada correctamente');
    } catch (error) {
      toast.error(extractErrorMessage(error));
    }
  }

  async function handleConfirmDelete() {
    if (!deletingId) return;
    try {
      await deleteObservation.mutateAsync(deletingId);
      setDeletingId(null);
      hapticSuccess();
      toast.success('Observación eliminada correctamente');
    } catch (error) {
      toast.error(extractErrorMessage(error));
    }
  }

  if (requirementQuery.isPending) {
    return (
      <MobileLayout title="Requerimiento" showBack>
        <CardListSkeleton count={3} />
      </MobileLayout>
    );
  }

  if (requirementQuery.isError || !requirement) {
    return (
      <MobileLayout title="Requerimiento" showBack>
        <EmptyState
          icon="error"
          title="No se pudo cargar"
          description="Verifica tu conexión e intenta de nuevo."
          actionLabel="Reintentar"
          onAction={() => void requirementQuery.refetch()}
        />
      </MobileLayout>
    );
  }

  const attachments = attachmentsQuery.data ?? [];
  const observations = observationsQuery.data ?? [];
  const contextActions = contextQuery.data?.actions ?? [];
  const contextPoints = contextQuery.data?.points ?? [];
  const documentRevision = contextQuery.data?.document_revision ?? requirement.workflow_revision;

  return (
    <MobileLayout title={requirement.code || 'Requerimiento'} subtitle={requirement.name} showBack>
      <div className="space-y-4">
        <SectionCard
          title="Datos generales"
          action={<StatusBadge status={requirement.status} size="sm" />}
        >
          <dl className="divide-y divide-border-light text-sm">
            <div className="flex items-center justify-between gap-3 py-2">
              <dt className="shrink-0 text-text-tertiary">Codigo</dt>
              <dd title={requirement.code} className="min-w-0 flex-1 truncate text-right font-semibold text-text-primary">
                {requirement.code || '—'}
              </dd>
            </div>
            <div className="flex items-center justify-between gap-3 py-2">
              <dt className="shrink-0 text-text-tertiary">Cliente</dt>
              <dd className="min-w-0 flex-1 break-words text-right font-medium text-text-primary">
                {requirement.client?.legal_name ?? '—'}
              </dd>
            </div>
            {requirement.client?.tax_id && (
              <div className="flex items-center justify-between gap-3 py-2">
                <dt className="shrink-0 text-text-tertiary">RUC</dt>
                <dd title={requirement.client.tax_id} className="min-w-0 flex-1 truncate text-right tabular-nums text-text-secondary">
                  {requirement.client.tax_id}
                </dd>
              </div>
            )}
            <div className="flex items-center justify-between gap-3 py-2">
              <dt className="shrink-0 text-text-tertiary">Tipo</dt>
              <dd className="min-w-0 flex-1 truncate text-right text-text-secondary">
                {typeLabel(requirement.type)}
              </dd>
            </div>
            <div className="flex items-center justify-between gap-3 py-2">
              <dt className="shrink-0 text-text-tertiary">Solicitado</dt>
              <dd className="min-w-0 flex-1 truncate text-right tabular-nums text-text-secondary">
                {formatDate(requirement.requested_date)}
              </dd>
            </div>
            <div className="flex items-center justify-between gap-3 py-2">
              <dt className="shrink-0 text-text-tertiary">Requerido</dt>
              <dd className="min-w-0 flex-1 truncate text-right tabular-nums text-text-secondary">
                {formatDate(requirement.required_date)} · {formatRelativeDate(requirement.required_date)}
              </dd>
            </div>
          </dl>
          {requirement.description && (
            <p className="mt-3 break-words border-t border-border-light pt-3 text-sm text-text-secondary">
              {requirement.description}
            </p>
          )}
          {canEdit && (
            <div className="mt-4">
              <MobileButton
                variant="secondary"
                size="md"
                fullWidth
                icon={<MobileIcon name="edit_note" className="h-5 w-5" />}
                onClick={() => navigate(`/m/requirements/${requirement.id}/edit`)}
              >
                Editar
              </MobileButton>
            </div>
          )}
        </SectionCard>

        {contextActions.length > 0 && (
          <SectionCard
            title="Acciones"
            subtitle="El servidor habilito estas acciones para este requerimiento."
          >
            <ServerActionRunner
              actions={contextActions}
              documentRevision={documentRevision}
              allowed={MOBILE_REQUIREMENT_ACTIONS}
              onExecute={handleExecuteAction}
              onSuccess={handleActionSuccess}
              onConflictReload={() => {
                void requirementQuery.refetch();
                void contextQuery.refetch();
              }}
            />
          </SectionCard>
        )}

        {contextPoints.length > 0 && (
          <SectionCard title="Recorrido" subtitle="Puntos del flujo vigente, solo lectura.">
            <ol className="space-y-2">
              {contextPoints.map((point) => (
                <li
                  key={point.code}
                  className="flex min-h-[52px] items-center gap-2 rounded-xl bg-bg-tertiary px-4 py-2"
                >
                  <MobileIcon name="flag" className="h-5 w-5 shrink-0 text-primary" />
                  <span className="min-w-0 flex-1 break-words text-sm font-semibold text-text-primary line-clamp-2">
                    {point.label}
                  </span>
                </li>
              ))}
            </ol>
          </SectionCard>
        )}

        <SectionCard
          title={`Observaciones (${observations.length})`}
          subtitle="Observar y resolver quedan en la web."
        >
          {observationsQuery.isPending && <CardListSkeleton count={2} />}
          {observationsQuery.isError && (
            <EmptyState
              icon="error"
              title="No se pudieron cargar las observaciones"
              description="Verifica tu conexión e intenta de nuevo."
              actionLabel="Reintentar"
              onAction={() => void observationsQuery.refetch()}
            />
          )}
          {!observationsQuery.isPending && !observationsQuery.isError && observations.length === 0 && (
            <div className="mb-3 rounded-xl bg-bg-tertiary p-4 text-center">
              <p className="truncate text-sm font-semibold text-text-primary">Sin observaciones</p>
              <p className="text-sm text-text-secondary">Agrega la primera si el pedido lo necesita.</p>
            </div>
          )}
          {!observationsQuery.isPending && !observationsQuery.isError && observations.length > 0 && (
            <ul className="mb-3 space-y-2">
              {observations.map((observation) => (
                <li key={observation.id} className="rounded-xl bg-bg-tertiary px-4 py-3">
                  {editingId === observation.id ? (
                    <div className="space-y-2">
                      <MobileTextarea
                        label="Observación"
                        rows={3}
                        value={editingComment}
                        onChange={(e) => setEditingComment(e.target.value)}
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <MobileButton
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setEditingId(null);
                            setEditingComment('');
                          }}
                        >
                          Cancelar
                        </MobileButton>
                        <MobileButton
                          variant="primary"
                          size="sm"
                          loading={updateObservation.isPending}
                          onClick={() => void handleSaveEditing(observation.id)}
                        >
                          Guardar
                        </MobileButton>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-2">
                      <span className="min-w-0 flex-1">
                        <span className="block break-words text-sm text-text-primary">
                          {observation.comment}
                        </span>
                        <span className="mt-0.5 block truncate text-xs text-text-tertiary">
                          {observation.status}
                          {observation.created_at ? ` · ${formatDate(observation.created_at)}` : ''}
                        </span>
                      </span>
                      {canObserve && observation.status !== 'resolved' && (
                        <span className="flex shrink-0 gap-1">
                          <button
                            type="button"
                            aria-label={`Editar observación ${observation.id}`}
                            onClick={() => startEditing(observation.id, observation.comment)}
                            className="flex h-11 w-11 items-center justify-center rounded-full text-text-secondary transition-colors hover:bg-border-light"
                          >
                            <MobileIcon name="edit_note" className="h-5 w-5" />
                          </button>
                          <button
                            type="button"
                            aria-label={`Eliminar observación ${observation.id}`}
                            onClick={() => setDeletingId(observation.id)}
                            className="flex h-11 w-11 items-center justify-center rounded-full text-error transition-colors hover:bg-error-light"
                          >
                            <MobileIcon name="cancel" className="h-5 w-5" />
                          </button>
                        </span>
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
          {canObserve && (
            <div className="space-y-2">
              <MobileTextarea
                label="Nueva observación"
                rows={2}
                placeholder="Describe la observación (mínimo 3 caracteres)..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
              />
              {commentError && <p className="break-words text-sm text-error">{commentError}</p>}
              <MobileButton
                variant="secondary"
                size="md"
                fullWidth
                loading={createObservation.isPending}
                icon={<MobileIcon name="add" className="h-5 w-5" />}
                onClick={() => void handleCreateObservation()}
              >
                Agregar observación
              </MobileButton>
            </div>
          )}
        </SectionCard>

        <SectionCard title={`Adjuntos (${attachments.length})`}>
          {attachmentsQuery.isPending && <CardListSkeleton count={2} />}
          {attachmentsQuery.isError && (
            <EmptyState
              icon="error"
              title="No se pudieron cargar los adjuntos"
              description="Verifica tu conexión e intenta de nuevo."
              actionLabel="Reintentar"
              onAction={() => void attachmentsQuery.refetch()}
            />
          )}
          {!attachmentsQuery.isPending && !attachmentsQuery.isError && attachments.length === 0 && (
            <div className="mb-3 rounded-xl bg-bg-tertiary p-4 text-center">
              <p className="truncate text-sm font-semibold text-text-primary">Sin adjuntos</p>
              <p className="text-sm text-text-secondary">Agrega fotos o archivos del pedido.</p>
            </div>
          )}
          {!attachmentsQuery.isPending && !attachmentsQuery.isError && attachments.length > 0 && (
            <ul className="mb-3 space-y-2">
              {attachments.map((attachment) => (
                <li
                  key={attachment.id}
                  className="flex min-h-[52px] items-center gap-2 rounded-xl bg-bg-tertiary px-4 py-2"
                >
                  <MobileIcon name="folder_open" className="h-5 w-5 shrink-0 text-text-tertiary" />
                  <span className="min-w-0 flex-1">
                    <span title={attachment.file_name} className="block truncate text-sm font-semibold text-text-primary">
                      {attachment.file_name}
                    </span>
                    {attachment.description && (
                      <span className="block break-words text-xs text-text-secondary line-clamp-2">
                        {attachment.description}
                      </span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <FileCapture onFiles={(files) => void handleFiles(files)} disabled={uploadMutation.isPending} />
          {uploadMutation.isPending && (
            <div className="mt-2 flex items-center gap-2 rounded-2xl bg-info-light px-4 py-3">
              <MobileIcon name="sync" className="h-5 w-5 shrink-0 animate-spin text-info-dark" />
              <p className="min-w-0 flex-1 truncate text-sm text-info-dark">Subiendo adjuntos...</p>
            </div>
          )}
          {uploadError && (
            <div className="mt-2 flex items-center gap-2 rounded-2xl bg-error-light px-4 py-3">
              <MobileIcon name="error" className="h-5 w-5 shrink-0 text-error" />
              <p className="min-w-0 flex-1 break-words text-sm text-error">{uploadError}</p>
            </div>
          )}
        </SectionCard>
      </div>

      {deletingId && (
        <ConfirmDialog
          title="Eliminar observación"
          message="¿Confirmas eliminar esta observación? Esta acción no se puede deshacer."
          confirmLabel="Eliminar"
          danger
          busy={deleteObservation.isPending}
          onConfirm={() => void handleConfirmDelete()}
          onCancel={() => setDeletingId(null)}
        />
      )}
    </MobileLayout>
  );
}
